import { normalizeFunnelStateCode } from "./funnelSessionSummary";
import { resolvePaidAttribution } from "../paidAttribution";
import { EXPERIENCE_EVENT_NAMES } from "../experience/experienceTelemetryRegistry";

export const FUNNEL_SCHEMA_VERSION = 1;

export const TRUSTED_FUNNEL_CONVERSION_EVENT_NAMES = new Set([
  "member_account_created",
  "member_onboarding_completed",
  "member_membership_paid",
  "member_membership_refunded",
  "member_visit_booked",
  "member_visit_request_created",
  "member_visit_request_helper_interacted",
  "member_visit_request_claimed",
  "member_visit_request_expired",
  "member_visit_request_cancelled",
  "member_visit_request_completed",
  "member_visit_request_refunded",
  "member_visit_refunded",
]);

export const TRUSTED_FUNNEL_BACKEND_EVENT_NAMES = new Set([
  ...TRUSTED_FUNNEL_CONVERSION_EVENT_NAMES,
  "member_membership_refund_feedback_saved",
  "member_membership_refund_retention_claimed",
  "member_membership_refunded_after_retention",
  "member_membership_contribution_decided",
  "pre_onboarding_pwyc_price_selected",
  "membership_offer_displayed",
  "membership_offer_viewed",
  "member_membership_checkout_viewed",
  "member_same_day_offer_applied",
  "member_same_day_offer_redeemed",
  "share_email_line_queued",
]);

export const FUNNEL_CLIENT_EVENT_NAMES = new Set([
  "landing_viewed",
  "landing_engagement_milestone",
  "landing_section_visible",
  "landing_section_entered",
  "landing_section_meaningfully_viewed",
  "landing_section_dwell_summary",
  "landing_scroll_depth_reached",
  "landing_qualified_active_engagement",
  "landing_cta_viewed",
  "landing_cta_clicked",
  "landing_highlighted_helper_card_clicked",
  "landing_anchor_nav_clicked",
  "member_seo_location_selected",
  "landing_expand_toggled",
  "landing_video_interaction",
  "landing_chat_opened",
  "landing_chat_suggestion_selected",
  "public_assistant_opened",
  "public_assistant_suggestions_displayed",
  "public_assistant_suggestion_selected",
  "public_assistant_user_turn",
  "public_assistant_response",
  "public_assistant_link_clicked",
  "public_assistant_action",
  "public_assistant_reliability_warning",
  "public_assistant_session_summary",
  "landing_pre_onboarding_opened",
  "qr_flyer_scan_detected",
  "share_prompt_viewed",
  "share_prompt_clicked",
  "share_prompt_selected",
  "share_hub_opened",
  "share_hub_dwell_summary",
  "share_email_line_opened",
  "share_qr_scan_detected",
  "share_link_opened",
  "share_tip_viewed",
  "share_tip_advanced",
  "share_action_viewed",
  "share_action_selected",
  "share_action_started",
  "share_action_completed",
  "share_action_handoff",
  "share_action_failed",
  "share_action_cancelled",
  "share_print_dialog_opened",
  "share_print_dialog_closed",

  "pre_onboarding_opened",
  "pre_onboarding_closed",
  "pre_onboarding_step_viewed",
  "pre_onboarding_step_advanced",
  "pre_onboarding_step_back",
  "pre_onboarding_step_skipped",
  "pre_onboarding_choice_changed",
  "pre_onboarding_chat_opened",
  "pre_onboarding_sophia_chat_engaged",
  "pre_onboarding_location_checked",
  "pre_onboarding_email_checked",
  "pre_onboarding_account_contact_entry_evidenced",
  "pre_onboarding_existing_account_handoff",
  "pre_onboarding_role_exit",
  "pre_onboarding_validation_failed",
  "pre_onboarding_submit_started",
  "pre_onboarding_submit_failed",
  "pre_onboarding_submit_succeeded",
  "pre_onboarding_runtime_event",
  "member_dashboard_availability_viewed",
  "account_access_failed",
  "account_access_recovery_completed",
  "checkout_runtime_event",
  "helper_visit_request_hidden",
  ...EXPERIENCE_EVENT_NAMES,
]);

export const FUNNEL_EVENT_NAMES = new Set([
  ...FUNNEL_CLIENT_EVENT_NAMES,
  ...TRUSTED_FUNNEL_BACKEND_EVENT_NAMES,
]);

export const isTrustedFunnelConversionEventName = (eventName = "") =>
  TRUSTED_FUNNEL_CONVERSION_EVENT_NAMES.has(String(eventName || ""));

export const isTrustedFunnelBackendEventName = (eventName = "") =>
  TRUSTED_FUNNEL_BACKEND_EVENT_NAMES.has(String(eventName || ""));

export const FUNNEL_ERROR_CODES = new Set([
  "email_invalid",
  "email_exists",
  "phone_invalid",
  "phone_exists",
  "phone_preflight_unavailable",
  "location_required",
  "name_invalid",
  "terms_not_accepted",
  "unknown_validation_failure",
  "storage_unavailable",
  "auth_failed",
  "backend_failed",
  "unknown_submit_failure",
  "auth_bootstrap_collision",
  "auth_network_unavailable",
  "auth_popup_blocked",
  "auth_provider_failure",
  "auth_rate_limited",
  "claims_refresh_failed",
  "helper_recovery_failed",
  "member_recovery_failed",
  "permission_denied",
  "role_conflict",
  "role_evidence_unavailable",
  "unknown_access_failure",
]);

const STRING_KEYS = new Set([
  "action",
  "actionId",
  "assignmentStatus",
  "availabilityState",
  "assistantSurface",
  "assistantVersion",
  "responseMode",
  "anchorSectionKey",
  "browserFamily",
  "bookingMode",
  "campaign",
  "campaignId",
  "adSetId",
  "adId",
  "cardVariantKey",
  "cardId",
  "city",
  "contentVariantKey",
  "copyRevision",
  "checkoutOutcome",
  "checkoutQuoteStatus",
  "checkoutStage",
  "checkoutSurface",
  "conversionSource",
  "closeReason",
  "ctaId",
  "tipId",
  "ctaPositionVariantKey",
  "desktopCtaPlacementKey",
  "desktopHeroMediaVariantKey",
  "destination",
  "deviceCategory",
  "entrySource",
  "emailLineVariantKey",
  "emailLineRevision",
  "assignmentUnit",
  "errorCategory",
  "errorName",
  "errorCode",
  "exitRegime",
  "flow",
  "flowEntrySource",
  "flowInstanceId",
  "requestId",
  "fieldName",
  "failureOwner",
  "flyerInstanceId",
  "flyerVersion",
  "subtitleRevision",
  "subtitleVariantKey",
  "flushReason",
  "fromStepId",
  "acquisitionProvider",
  "acquisitionSource",
  "acquisitionSelfReportDisagreementKind",
  "attributionBasis",
  "attributionModelVersion",
  "confidence",
  "conversionAttributionBasis",
  "conversionConfidence",
  "conversionProvider",
  "contributionDecision",
  "contributionStatus",
  "helperCountBucket",
  "futureBookableHoursBucket",
  "availabilityMeasurementSource",
  "helperLobbyTreatment",
  "helperDiscountBookingBucket",
  "helperDiscountEligibilityMode",
  "helperDiscountQuotaPeriod",
  "helperDiscountType",
  "helperId",
  "inventoryVersion",
  "modelRunId",
  "selectionId",
  "intentType",
  "issue",
  "job",
  "labelKey",
  "layoutVariantKey",
  "locationSource",
  "founderPlacementKey",
  "matchType",
  "membershipAmountBucket",
  "pwycBookingOrdinal",
  "pwycBookedChoice",
  "pwycFirstBookedChoice",
  "membershipLifecycle",
  "membershipTreatmentKind",
  "membershipAccessKind",
  "membershipAccessSource",
  "previousContributionDecision",
  "currentContributionDecision",
  "memberStage",
  "medium",
  "mediaVariantKey",
  "mobileCtaPlacementKey",
  "mobileHeroMediaLayoutVariantKey",
  "mobileHeroMediaOrderKey",
  "heroMediaFrameKey",
  "nearbyDemandBucket",
  "numberType",
  "outcome",
  "operation",
  "pagePath",
  "originalRoutePath",
  "paidMediaProvider",
  "paymentMethodMode",
  "taxCollectionStatus",
  "placeGeoId",
  "presetKey",
  "pricingVersion",
  "promoKey",
  "questionChoice",
  "referralCode",
  "referralSource",
  "refundAmountBucket",
  "refundReasonCode",
  "refundType",
  "requestLifecycleOutcome",
  "requestLifecycleStatus",
  "requestFlexibilityMode",
  "requestStatus",
  "responseOutcome",
  "reasonCode",
  "severity",
  "route",
  "routeKind",
  "routePath",
  "googleReferrerSeoEntryPath",
  "sectionKey",
  "sectionOrderBackgroundKey",
  "sectionOrderCandidatePoolKey",
  "sectionOrderKey",
  "sectionOrderRankerVersion",
  "sectionOrderSlot",
  "sectionOrderVariantKey",
  "seoCohort",
  "seoExperimentKey",
  "seoVersion",
  "serviceStatus",
  "skipReason",
  "source",
  "sourceFamily",
  "platform",
  "inputLengthBucket",
  "inputMode",
  "linkTargetId",
  "linkTargetKind",
  "suggestionSetKey",
  "suggestionSource",
  "state",
  "stateCode",
  "stage",
  "signupRole",
  "stepId",
  "surface",
  "telemetryContract",
  "channel",
  "taskGroup",
  "taskLabel",
  "taskSlug",
  "threshold",
  "timingKey",
  "toStepId",
  "transportPhase",
  "trustSignalVariantKey",
  "videoId",
  "visitId",
  "valueMetricFirstPaidEligibility",
  "visitRequestInteractionType",
  "viewportBucket",
]);

const NUMBER_KEYS = new Set([
  "futureBookableHours",
  "helpersWithFutureHoursCount",
  "helpersWith20FutureHoursCount",
  "contributionAmountCents",
  "checkoutTotalCents",
  "taxAmountCents",
  "httpStatus",
  "previousContributionAmountCents",
  "currentContributionAmountCents",
  "ctaPosition",
  "tipPosition",
  "activeMs",
  "foregroundMs",
  "assistantTurnCount",
  "credentialLatencyMs",
  "availableDayCount",
  "availableHelperCount",
  "availableSlotCount",
  "cumulativeMs",
  "currentTime",
  "duration",
  "durationMinutes",
  "displayLatencyMs",
  "discountHours",
  "helperDiscountCount",
  "helperDiscountEstimatedSavingsCents",
  "helperDiscountReservedCount",
  "helpersAcceptedCount",
  "helpersDeclinedCount",
  "helpersHiddenCount",
  "helpersNeededRemaining",
  "helpersRequestedCount",
  "helpersTargetedCount",
  "firstPaidClickAtMs",
  "firebaseAppCheckLatencyMs",
  "firebaseIdTokenLatencyMs",
  "latestPaidClickAtMs",
  "latencyMs",
  "composeDurationMs",
  "guidedTurnCount",
  "failureCount",
  "focusLifetimeMs",
  "infoOpenCount",
  "linkClickCount",
  "openCount",
  "openDurationMs",
  "providerLatencyMs",
  "requestLatencyMs",
  "responseBodyLatencyMs",
  "hedgeDelayMs",
  "modelAttemptCount",
  "modelWinnerLatencyMs",
  "readDurationMs",
  "responseCount",
  "responseWordCount",
  "selectedIndex",
  "sessionDurationMs",
  "suggestionCount",
  "suggestionVisibleMs",
  "totalTurnCount",
  "typedTurnCount",
  "userTurnCount",
  "viewportHeight",
  "viewportWidth",
  "inputWordCount",
  "mediaErrorCode",
  "minimumHourlyRateCents",
  "normalizedRangePercent",
  "normalizedRangePosition",
  "offerCostCents",
  "openRequestExpirationDays",
  "openRequestFlexibleOptionCount",
  "openRequestOptionCount",
  "frequency",
  "maxIntersectionRatio",
  "maxScrollDepthPct",
  "progressPct",
  "questionIndex",
  "readyState",
  "sectionIndex",
  "sectionOrderPosition",
  "sectionOrderSlotIndex",
  "seconds",
  "selectedCount",
  "stepCount",
  "stepIndex",
  "timeOnPreviousStepMs",
  "timeOnStepMs",
  "toStepIndex",
  "fromStepIndex",
  "viewedSeconds",
  "visibleThreshold",
  "networkState",
  "numHelpers",
  "attemptCount",
  "winningModelAttempt",
  "retryCount",
  "selectedHourlyRateCents",
  "selectedReasonCount",
  "spreadCents",
  "suggestedHourlyRateCents",
]);

const BOOLEAN_KEYS = new Set([
  "promptAgain",
  "pwycPaidAfterFirstSkip",
  "deferredRendered",
  "expanded",
  "hasCoords",
  "hasCheckoutSnapshot",
  "hasAdClickId",
  "hasEmail",
  "hasDisplayLine",
  "hasFbclid",
  "hasGoogleClickId",
  "hasBookableAvailability",
  "hasFirstName",
  "hasLastName",
  "hasMetaClickId",
  "hasPhone",
  "hasReferralCode",
  "hasSelectedVisitTime",
  "hasWrittenFeedback",
  "graceEnabled",
  "hedgeTriggered",
  "hedgeWon",
  "helperDiscountPresent",
  "helperDiscountQuotaLimited",
  "helperDiscountReserved",
  "isResume",
  "platformBlocker",
  "recovered",
  "partialRefund",
  "muted",
  "playbackStarted",
  "skipped",
  "watchSatisfied",
  "conversationStopped",
  "fallbackUsed",
  "fallbackActivated",
  "linkShown",
  "firstVisit",
  "sameDayOfferApplied",
  "sameDayOfferEligible",
  "sameDayOfferVisible",
  "sameDaySignup",
  "slowObserved",
  "termsAccepted",
  "waitlistLike",
  "acquisitionConversionDisagreement",
]);

const ARRAY_KEYS = new Set([
  "experimentKeys",
  "experimentPairs",
  "assistedProviders",
  "clickIdTypes",
  "conversationScores",
  "selectedKeys",
  "sectionOrderVisibleSectionKeys",
  "sectionOrderVisibleSlotKeys",
  "sectionOrderViewedSectionKeys",
  "sectionOrderViewedSlotKeys",
  "helpTypeKeys",
  "priorityKeys",
  "suggestionIds",
  "visibleSuggestionIds",
  "variantKeys",
]);

const ALLOWED_PROPERTY_KEYS = new Set([
  "cardVisibleMs",
  ...STRING_KEYS,
  ...NUMBER_KEYS,
  ...BOOLEAN_KEYS,
  ...ARRAY_KEYS,
]);

const PII_KEY_PATTERN =
  /(^|[._-])(address|email|firstName|lastName|fullName|name|phone|sms|text|description|message|taskInput|ip|fbclid|gclid|wbraid|gbraid|token|secret)($|[._-])/i;

const MAX_PROPERTY_KEYS = 48;
const MAX_NESTED_KEYS = 20;
const MAX_PROPERTY_DEPTH = 2;
const MAX_PROPERTY_BYTES = 6000;
const SAFE_PROPERTY_KEY_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]{0,79}$/;
const SAFE_NESTED_KEY_PATTERN = /^[a-zA-Z0-9_-]{1,80}$/;
const BLOCKED_CUSTOM_PROPERTY_KEYS = new Set([
  "client_secret",
  "fbclid",
  "gbraid",
  "gclid",
  "ip",
  "name",
  "payment_method_id",
  "wbraid",
]);
const BLOCKED_CUSTOM_PROPERTY_TOKEN_PATTERN =
  /(^|_)(address|amount|click_id|cost|cta|description|email|financial|first_name|full_name|headline|last_name|message|name|note|payment|payload|phone|price|rate|raw|refund|revenue|secret|sms|task_input|tax|text|token|traffic)($|_)/i;

const cleanText = (value, maxLength = 120) =>
  String(value ?? "")
    .replace(/\s+/g, "_")
    .trim()
    .slice(0, maxLength);

const cleanLocationLabel = (value, maxLength = 120) =>
  String(value ?? "")
    .replace(/[\u2018\u2019\u201B\u2032\u02BC]/gu, "'")
    .replace(/[^\p{Letter}\p{Number}\s.'-]/gu, " ")
    .replace(/\s+/g, "_")
    .trim()
    .slice(0, maxLength);

const hasPiiShape = (value = "") => {
  const text = String(value || "");
  if (!text) return false;
  if (text.includes("@")) return true;
  const digits = text.replace(/\D/g, "");
  return digits.length >= 7;
};

const safeIdentifier = (value, { allowLongNumeric = false } = {}) => {
  const text = cleanText(value);
  if (!text) return null;
  if (!allowLongNumeric && hasPiiShape(text)) return null;
  if (!/^[a-zA-Z0-9_.:/?#&=-]+$/.test(text)) return null;
  return text;
};

const safeActivitySessionId = (value) => {
  const text = cleanText(value);
  return /^[a-zA-Z0-9_-]{8,160}$/.test(text) ? text : null;
};

const safeLocationLabel = (value) => {
  const text = cleanLocationLabel(value);
  if (!text || hasPiiShape(text)) return null;
  return text;
};

const safeNumber = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return Math.round(number * 1000) / 1000;
};

const safeArray = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => safeIdentifier(item))
    .filter(Boolean)
    .slice(0, 20);
};

const safeConversationScores = (value) => {
  if (!Array.isArray(value) || value.length !== 4) return [];
  return value.map((item) => {
    const number = Number(item);
    return Number.isFinite(number)
      ? Math.max(0, Math.min(9, Math.round(number)))
      : 0;
  });
};

const normalizePropertyKeyForInspection = (key = "") =>
  String(key || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase();

const isSafeCustomPropertyKey = (key = "") => {
  if (!SAFE_PROPERTY_KEY_PATTERN.test(key)) return false;
  const normalized = normalizePropertyKeyForInspection(key);
  return (
    !BLOCKED_CUSTOM_PROPERTY_KEYS.has(normalized) &&
    !BLOCKED_CUSTOM_PROPERTY_TOKEN_PATTERN.test(normalized)
  );
};

export const isSafeFunnelPropertyKey = (key = "") =>
  ALLOWED_PROPERTY_KEYS.has(key) || isSafeCustomPropertyKey(key);

const isSafeNestedKey = (key = "") => {
  if (!SAFE_NESTED_KEY_PATTERN.test(key)) return false;
  const normalized = normalizePropertyKeyForInspection(key);
  return (
    !BLOCKED_CUSTOM_PROPERTY_KEYS.has(normalized) &&
    !BLOCKED_CUSTOM_PROPERTY_TOKEN_PATTERN.test(normalized)
  );
};

const isPlainObject = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const allowsNumericIdentifierShape = (key = "") =>
  /(date|millis|timestamp|time)$/i.test(String(key || ""));

const sanitizeRichPropertyValue = (value, { depth = 0, key = "" } = {}) => {
  if (typeof value === "string") {
    return safeIdentifier(value, {
      allowLongNumeric: allowsNumericIdentifierShape(key),
    });
  }
  if (typeof value === "number") return safeNumber(value);
  if (typeof value === "boolean") return value;
  if (depth >= MAX_PROPERTY_DEPTH) return null;

  if (Array.isArray(value)) {
    const sanitized = value
      .slice(0, 20)
      .map((item) => sanitizeRichPropertyValue(item, { depth: depth + 1, key }))
      .filter((item) => item !== null);
    return sanitized.length ? sanitized : null;
  }

  if (!isPlainObject(value)) return null;
  const sanitized = {};
  Object.entries(value)
    .slice(0, MAX_NESTED_KEYS)
    .forEach(([nestedKey, nestedValue]) => {
      if (!isSafeNestedKey(nestedKey)) return;
      const next = sanitizeRichPropertyValue(nestedValue, {
        depth: depth + 1,
        key: nestedKey,
      });
      if (next !== null) sanitized[nestedKey] = next;
    });
  return Object.keys(sanitized).length ? sanitized : null;
};

const LONG_NUMERIC_IDENTIFIER_KEYS = new Set([
  "campaignId",
  "adSetId",
  "adId",
  "flowInstanceId",
  "inventoryVersion",
  "modelRunId",
  "selectionId",
  "job",
  "placeGeoId",
  "seoVersion",
  "videoId",
  "visitId",
]);

const LOCATION_LABEL_KEYS = new Set(["city", "state"]);

export const sanitizeFunnelProperties = (properties = {}) => {
  if (!properties || typeof properties !== "object") return {};

  return Object.entries(properties)
    .slice(0, MAX_PROPERTY_KEYS)
    .reduce((acc, [key, value]) => {
      const registeredKey = ALLOWED_PROPERTY_KEYS.has(key);
      if (!isSafeFunnelPropertyKey(key)) return acc;
      if (!registeredKey && PII_KEY_PATTERN.test(key)) return acc;

      if (STRING_KEYS.has(key)) {
        const allowLongNumeric = LONG_NUMERIC_IDENTIFIER_KEYS.has(key);
        const sanitized = LOCATION_LABEL_KEYS.has(key)
          ? safeLocationLabel(value)
          : safeIdentifier(value, { allowLongNumeric });
        if (sanitized) acc[key] = sanitized;
        return acc;
      }

      if (NUMBER_KEYS.has(key)) {
        const sanitized = safeNumber(value);
        if (sanitized !== null) acc[key] = sanitized;
        return acc;
      }

      if (BOOLEAN_KEYS.has(key)) {
        acc[key] = Boolean(value);
        return acc;
      }

      if (ARRAY_KEYS.has(key)) {
        const sanitized =
          key === "conversationScores"
            ? safeConversationScores(value)
            : safeArray(value);
        if (sanitized.length) acc[key] = sanitized;
        return acc;
      }

      const sanitized = sanitizeRichPropertyValue(value, { key });
      if (sanitized === null) return acc;
      const candidate = { ...acc, [key]: sanitized };
      if (JSON.stringify(candidate).length <= MAX_PROPERTY_BYTES) {
        acc[key] = sanitized;
      }

      return acc;
    }, {});
};

export const normalizeFunnelSource = (value = "") => {
  const source = String(value || "")
    .trim()
    .toLowerCase();
  if (!source) return "";
  if (["fb", "facebook", "meta"].includes(source) || source.startsWith("fb")) {
    return "facebook";
  }
  if (["ig", "instagram"].includes(source)) return "instagram";
  if (["an", "audience_network", "audiencenetwork"].includes(source)) {
    return "audience_network";
  }
  if (["google", "google_ads", "adwords"].includes(source)) return "google";
  if (/^\d{10,}$/.test(source)) return "meta_numeric";
  return safeIdentifier(source) || "other";
};

export const getSourceFamily = (source = "") => {
  if (
    ["facebook", "instagram", "audience_network", "meta_numeric"].includes(
      source,
    )
  ) {
    return "meta";
  }
  if (source === "google") return "google";
  if (!source || source === "blank") return "unknown";
  return "other";
};

const firstSafeCampaignValue = (...values) => {
  for (const value of values) {
    const sanitized = safeIdentifier(value, { allowLongNumeric: true });
    if (sanitized) return sanitized;
  }
  return null;
};

export const buildFunnelAttribution = ({
  utmSource = null,
  storedAttribution = null,
  locationData = null,
  browserEntry = null,
} = {}) => {
  const clickIds = storedAttribution?.clickIds || {};
  const storedUtm = storedAttribution?.utm || {};
  const paidAttribution = resolvePaidAttribution({ storedAttribution });
  const hasFbclid = Boolean(clickIds.fbclid);
  const hasGoogleClick = Boolean(
    clickIds.gclid || clickIds.gbraid || clickIds.wbraid,
  );
  const storedSource = storedAttribution?.source || storedUtm.utm_source;
  const routeSource = normalizeFunnelSource(locationData?.source);
  const isMemberSeoRoute =
    routeSource === "member_city_seo" ||
    String(locationData?.sourceFamily || "")
      .trim()
      .toLowerCase() === "member_seo";
  const seoRoutePath = String(locationData?.routePath || "").replace(/\/+$/, "");
  const isGoogleSeoEntry = (() => {
    if (!isMemberSeoRoute || paidAttribution.primaryPaidProvider) return false;
    const entryPath = String(browserEntry?.path || "").replace(/\/+$/, "");
    if (!seoRoutePath || seoRoutePath !== entryPath) return false;
    try {
      return /^(?:(?:www|m)\.)?google\.(?:com|ca|co\.uk|com\.au)$/i.test(
        new URL(browserEntry.referrer).hostname,
      );
    } catch (_error) {
      return false;
    }
  })();
  const isHelperHiringSeoRoute =
    ["hiring", "hiring_locations", "hiring_state", "hiring_city"].includes(
      routeSource,
    ) ||
    String(locationData?.sourceFamily || "")
      .trim()
      .toLowerCase() === "helper_hiring_seo";
  const source =
    paidAttribution.acquisitionSource ||
    paidAttribution.primaryAdSource ||
    (isGoogleSeoEntry ? "member_city_seo" : null) ||
    normalizeFunnelSource(utmSource || storedSource) ||
    (isMemberSeoRoute
      ? "member_city_seo"
      : isHelperHiringSeoRoute
        ? routeSource || "hiring"
        : "blank");

  const attribution = {
    source,
    sourceFamily:
      source === "member_city_seo"
        ? "member_seo"
        : isHelperHiringSeoRoute && !paidAttribution.primaryPaidProvider
          ? "helper_hiring_seo"
          : paidAttribution.sourceFamily || getSourceFamily(source),
    hasFbclid,
  };
  if (isGoogleSeoEntry) {
    attribution.googleReferrerSeoEntryPath = seoRoutePath;
  }

  if (paidAttribution.primaryPaidProvider) {
    attribution.acquisitionProvider = paidAttribution.acquisitionProvider;
    attribution.acquisitionSource = paidAttribution.acquisitionSource;
    attribution.paidMediaProvider = paidAttribution.primaryPaidProvider;
    attribution.attributionModelVersion =
      paidAttribution.attributionModelVersion;
    attribution.attributionBasis = paidAttribution.attributionBasis;
    attribution.confidence = paidAttribution.confidence;
  }
  if (paidAttribution.conversionProvider) {
    attribution.conversionProvider = paidAttribution.conversionProvider;
    attribution.conversionSource = paidAttribution.conversionSource;
    attribution.conversionAttributionBasis =
      paidAttribution.conversionAttributionBasis;
    attribution.conversionConfidence = paidAttribution.conversionConfidence;
  }
  if (paidAttribution.acquisitionSelfReportDisagreementKind) {
    attribution.acquisitionSelfReportDisagreementKind =
      paidAttribution.acquisitionSelfReportDisagreementKind;
  }
  if (paidAttribution.acquisitionConversionDisagreement) {
    attribution.acquisitionConversionDisagreement = true;
  }
  if (paidAttribution.firstPaidClickAtMs) {
    attribution.firstPaidClickAtMs = paidAttribution.firstPaidClickAtMs;
  }
  if (paidAttribution.latestPaidClickAtMs) {
    attribution.latestPaidClickAtMs = paidAttribution.latestPaidClickAtMs;
  }
  if (paidAttribution.hasAdClickId) attribution.hasAdClickId = true;
  if (hasGoogleClick || paidAttribution.hasGoogleClickId) {
    attribution.hasGoogleClickId = true;
  }
  if (hasFbclid || paidAttribution.hasMetaClickId) {
    attribution.hasMetaClickId = true;
  }
  if (Array.isArray(paidAttribution.clickIdTypes)) {
    attribution.clickIdTypes = paidAttribution.clickIdTypes;
  }
  if (Array.isArray(paidAttribution.assistedProviders)) {
    attribution.assistedProviders = paidAttribution.assistedProviders;
  }

  // Keep source and IDs on the same first-paid touch. A later campaign must
  // never fill missing IDs on an earlier acquisition.
  const campaignId = firstSafeCampaignValue(
    paidAttribution.primaryPaidProvider ? paidAttribution.utmCampaign : storedUtm.utm_campaign,
    ...(!paidAttribution.primaryPaidProvider ? [storedAttribution?.campaignId] : []),
  );
  const adSetId = firstSafeCampaignValue(
    paidAttribution.primaryPaidProvider ? paidAttribution.utmTerm : storedUtm.utm_term,
    ...(!paidAttribution.primaryPaidProvider ? [storedAttribution?.adSetId] : []),
  );
  const adId = firstSafeCampaignValue(
    paidAttribution.primaryPaidProvider ? paidAttribution.utmContent : storedUtm.utm_content,
    ...(!paidAttribution.primaryPaidProvider ? [storedAttribution?.adId] : []),
  );

  if (campaignId) attribution.campaignId = campaignId;
  if (adSetId) attribution.adSetId = adSetId;
  if (adId) attribution.adId = adId;

  if (isMemberSeoRoute) {
    [
      ["taskSlug", locationData?.taskSlug],
      ["taskLabel", locationData?.taskLabel],
      ["taskGroup", locationData?.taskGroup],
      ["city", locationData?.city],
      [
        "state",
        locationData?.stateCode ||
          locationData?.state_code ||
          locationData?.region_code ||
          locationData?.state,
      ],
      ["placeGeoId", locationData?.placeGeoId],
      ["routePath", locationData?.routePath],
      ["seoVersion", locationData?.seoVersion],
      ["seoCohort", locationData?.seoCohort],
    ].forEach(([key, value]) => {
      if (value) attribution[key] = value;
    });
  }

  if (isHelperHiringSeoRoute) {
    [
      ["job", locationData?.job],
      ["city", locationData?.city],
      [
        "state",
        locationData?.stateCode ||
          locationData?.state_code ||
          locationData?.region_code ||
          locationData?.state,
      ],
      ["routePath", locationData?.routePath],
      ["originalRoutePath", locationData?.originalRoutePath],
      ["routeKind", locationData?.routeKind],
      ["seoVersion", locationData?.seoVersion],
      ["seoExperimentKey", locationData?.seoExperimentKey],
      ["seoCohort", locationData?.seoCohort],
    ].forEach(([key, value]) => {
      if (value) attribution[key] = value;
    });
  }

  return attribution;
};

export const buildFunnelContext = ({
  route = "",
  systemData = null,
  locationData = null,
} = {}) => {
  const screenSize = String(systemData?.screenSize || "");
  const [widthRaw] = screenSize.split("x");
  const width = Number(widthRaw);
  const browser = String(systemData?.browser || "").toLowerCase();
  const browserFamily = browser.includes("chrome")
    ? "chrome"
    : browser.includes("safari")
      ? "safari"
      : browser.includes("firefox")
        ? "firefox"
        : browser.includes("edge")
          ? "edge"
          : "unknown";

  const context = {
    route: safeIdentifier(route || "/") || "/",
    deviceCategory:
      safeIdentifier(systemData?.deviceCategory || "unknown") || "unknown",
    browserFamily,
    viewportBucket: Number.isFinite(width)
      ? width < 600
        ? "mobile"
        : width < 960
          ? "tablet"
          : "desktop"
      : "unknown",
  };

  const stateCode = normalizeFunnelStateCode(
    locationData?.stateCode,
    locationData?.state_code,
    locationData?.region_code,
    locationData?.state,
    locationData?.region,
  );
  if (/^[A-Z]{2}$/.test(stateCode)) context.stateCode = stateCode;

  const city = safeLocationLabel(
    locationData?.city || locationData?.locality || "",
  );
  if (city) context.city = city;

  const state = safeLocationLabel(
    locationData?.region ||
      locationData?.state ||
      locationData?.stateCode ||
      locationData?.state_code ||
      locationData?.region_code ||
      "",
  );
  if (state) context.state = state;

  const locationSource = safeIdentifier(
    locationData?.locationSource || locationData?.source || "",
  );
  if (locationSource) context.locationSource = locationSource;

  const routePath = safeIdentifier(locationData?.routePath || "");
  if (routePath) context.routePath = routePath;

  return context;
};

export const buildFunnelDedupeKey = ({
  eventName,
  sessionId,
  userId,
  memberId,
  key = "",
} = {}) =>
  [eventName, sessionId || userId || memberId || "anon", key]
    .filter(Boolean)
    .join("|");

export const buildFunnelEventPayload = ({
  eventName,
  eventType = "action",
  flow = "",
  surface = "",
  sessionId = null,
  activitySessionId = null,
  activitySessionQualified = false,
  userId = null,
  memberId = null,
  properties = {},
  attribution = {},
  context = {},
  dedupeKey = null,
  clientEventId = null,
  occurredAtClient = new Date(),
  sequence = null,
  allowTrustedConversion = false,
} = {}) => {
  if (!FUNNEL_EVENT_NAMES.has(eventName)) {
    throw new Error(`Unknown funnel event: ${eventName}`);
  }
  if (
    isTrustedFunnelBackendEventName(eventName) &&
    allowTrustedConversion !== true
  ) {
    throw new Error(
      `Trusted funnel event must be recorded server-side: ${eventName}`,
    );
  }

  const safeProperties = sanitizeFunnelProperties({
    ...properties,
    flow,
    surface,
  });

  return {
    schemaVersion: FUNNEL_SCHEMA_VERSION,
    eventName,
    eventType,
    flow,
    surface,
    sessionId: sessionId || null,
    activitySessionId: safeActivitySessionId(activitySessionId),
    activitySessionQualified: activitySessionQualified === true,
    userId: userId || null,
    memberId: memberId || null,
    properties: safeProperties,
    attribution: sanitizeFunnelProperties(attribution),
    context: sanitizeFunnelProperties(context),
    dedupeKey: dedupeKey || null,
    clientEventId: safeIdentifier(clientEventId, { allowLongNumeric: true }),
    sequence: Number.isFinite(Number(sequence)) ? Number(sequence) : null,
    occurredAtClient,
  };
};

export const bucketHelperCount = (count) => {
  const n = Number(count || 0);
  if (n <= 0) return "0";
  if (n <= 2) return "1_2";
  return "3_plus";
};

export const bucketNearbyDemand = (count) => {
  const n = Number(count || 0);
  if (n <= 0) return "0";
  if (n <= 5) return "1_5";
  if (n <= 20) return "6_20";
  return "20_plus";
};

export const bucketFutureBookableHours = (hours) => {
  const value = Number(hours);
  if (!Number.isFinite(value) || value < 0) return "unknown";
  if (value === 0) return "0";
  if (value < 10) return "under_10";
  if (value < 20) return "10_19";
  if (value < 40) return "20_39";
  if (value < 80) return "40_79";
  return "80_plus";
};
